import React, { useState, useEffect } from 'react';
import { Table, Typography, Card, Space, Button, message, Spin, Tag, Tooltip } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

interface FormSubmission {
  id: string;
  form_id: string;
  data: Record<string, any>;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

interface Form {
  id: string;
  name: string;
  website_id: string;
  fields: Array<{
    name: string;
    label: string;
    type: string;
    required: boolean;
  }>;
}

const FormSubmissions: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [formLoading, setFormLoading] = useState(true);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchFormDetails = async () => {
    setFormLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`);
      if (response.data.success) {
        setForm(response.data.data);
      } else {
        message.error(response.data.message || '获取表单信息失败');
      }
    } catch (error: any) {
      message.error('获取表单信息时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setFormLoading(false);
    }
  };

  const fetchSubmissions = async (page = 1) => {
    if (!id) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/forms/${id}/submissions?page=${page}&pageSize=${pagination.pageSize}`
      );
      
      if (response.data.success) {
        setSubmissions(response.data.data);
        setPagination({
          ...pagination,
          current: page,
          total: response.data.pagination.total,
        });
      } else {
        message.error(response.data.message || '获取表单提交记录失败');
      }
    } catch (error: any) {
      message.error('获取表单提交记录时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchFormDetails();
      fetchSubmissions();
    }
  }, [id]);

  const handleTableChange = (pagination: any) => {
    fetchSubmissions(pagination.current);
  };

  const renderSubmissionData = (data: any, fields: any[]) => {
    if (!data || !fields) return null;
    
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {fields.map((field, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '8px', fontWeight: 'bold', width: '30%' }}>{field.label}:</td>
              <td style={{ padding: '8px' }}>{data[field.name] || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const columns = [
    {
      title: 'IP地址',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 140,
    },
    {
      title: '提交时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '浏览器',
      dataIndex: 'user_agent',
      key: 'user_agent',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span>{text.substring(0, 50)}{text.length > 50 ? '...' : ''}</span>
        </Tooltip>
      ),
    },
  ];

  if (formLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="加载表单信息..." />
      </div>
    );
  }

  if (!form) {
    return (
      <div>
        <Title level={2}>表单不存在</Title>
        <Button type="primary" onClick={() => navigate('/forms')}>返回表单列表</Button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/forms')}
          >
            返回表单列表
          </Button>
        </Space>
      </div>

      <Card title={`表单提交记录 - ${form.name}`} style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 16 }}>
          <Text>共 {pagination.total} 条提交记录</Text>
        </div>

        <Table
          loading={loading}
          columns={columns}
          dataSource={submissions}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
          }}
          onChange={handleTableChange}
          expandable={{
            expandedRowRender: record => renderSubmissionData(record.data, form.fields),
          }}
        />
      </Card>
    </div>
  );
};

export default FormSubmissions; 